import React, {useState} from 'react';
import {Button, Flex, message, Modal, Select, Spin} from "antd";
import {CheckCircleFilled, CloseCircleFilled, SafetyCertificateOutlined} from "@ant-design/icons";
import {useSelector} from "react-redux";
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import CertificateService from "../../../../services/CertificateService";

// Выдача сертификатов всей группе прямо со страницы журнала.
// Кнопка только для админа: /api/admin/** преподавателю недоступен, да и сертификаты выдаёт админ.
// Саму генерацию делает существующий /save/by-group — сквозная нумерация идёт тем же единственным путём.
const GroupCertificatesButton = ({groupId}) => {
    const {user} = useSelector(state => state.user);
    const isAdmin = user?.role?.roleName === 'admin';

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [readiness, setReadiness] = useState(null);

    if (!isAdmin || !groupId) {
        return null;
    }

    async function loadReadiness(templateId) {
        setLoading(true);
        try {
            const r = await CertificateService.getGroupCertificateReadiness(groupId, templateId);
            setReadiness(r.data);
        } catch (e) {
            message.error(<IntlMessage id={'error'}/>, 5);
            setReadiness(null);
        } finally {
            setLoading(false);
        }
    }

    function openModal() {
        setOpen(true);
        setReadiness(null);
        loadReadiness(null);
    }

    function closeModal() {
        if (generating) {
            return;
        }
        setOpen(false);
    }

    async function generate() {
        if (!readiness?.ready || generating) {
            return;
        }
        setGenerating(true);
        try {
            const r = await CertificateService.saveCertificateGroup(readiness.templateId, groupId);
            const issued = Array.isArray(r.data) ? r.data.length : readiness.toIssue;
            message.success(<span><IntlMessage id={'groupCert.generated'}/>: {issued}</span>, 5);
            // перечитываем — теперь «уже выдано» вырастет, а «будет выдано» станет 0
            await loadReadiness(readiness.templateId);
        } catch (e) {
            message.error(<IntlMessage id={'error'}/>, 5);
        } finally {
            setGenerating(false);
        }
    }

    const conditionRow = (ok, labelId, extra) => (
        <Flex align={"center"} gap={8}>
            {ok
                ? <CheckCircleFilled style={{color: "#389e0d"}}/>
                : <CloseCircleFilled style={{color: "#cf1322"}}/>}
            <span><IntlMessage id={labelId}/>{extra ? `: ${extra}` : ''}</span>
        </Flex>
    );

    const countRow = (labelId, value, strong) => (
        <Flex justify={"space-between"} style={{width: "100%"}}>
            <span style={{color: "#666"}}><IntlMessage id={labelId}/></span>
            <span style={{fontWeight: strong ? 700 : 400}}>{value}</span>
        </Flex>
    );

    return (
        <>
            <Button icon={<SafetyCertificateOutlined/>} onClick={openModal}>
                <IntlMessage id={'groupCert.button'}/>
            </Button>

            <Modal
                width={520}
                open={open}
                maskClosable={false}
                closable={!generating}
                title={<IntlMessage id={'groupCert.title'}/>}
                onCancel={closeModal}
                footer={[
                    <Button key="cancel" onClick={closeModal} disabled={generating}>
                        <IntlMessage id={'cancel'}/>
                    </Button>,
                    <Button
                        key="generate"
                        type="primary"
                        loading={generating}
                        disabled={!readiness?.ready}
                        onClick={generate}
                    >
                        <IntlMessage id={'groupCert.generate'}/>
                    </Button>
                ]}
            >
                {loading && <Flex justify={"center"} style={{padding: 30}}><Spin/></Flex>}

                {!loading && readiness &&
                    <Flex vertical gap={16} style={{width: "100%"}}>
                        <b>{readiness.groupName}</b>

                        <Flex vertical gap={6}>
                            {conditionRow(
                                readiness.allLessonsConducted,
                                'groupCert.lessons',
                                `${readiness.lessonsConducted} / ${readiness.lessonsTotal}`
                            )}
                            {conditionRow(readiness.allGraded, 'groupCert.graded')}
                        </Flex>

                        <Flex vertical gap={4} style={{
                            background: "#fafafa",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: "10px 14px"
                        }}>
                            {countRow('groupCert.students', readiness.studentsTotal)}
                            {countRow('groupCert.passed', readiness.studentsPassed)}
                            {readiness.studentsFailed > 0 && countRow('groupCert.failed', readiness.studentsFailed)}
                            {readiness.alreadyIssued > 0 && countRow('groupCert.already', readiness.alreadyIssued)}
                            {readiness.studentsNoProfile > 0 && countRow('groupCert.noProfile', readiness.studentsNoProfile)}
                            {countRow('groupCert.toIssue', readiness.toIssue, true)}
                        </Flex>

                        <Flex vertical gap={6}>
                            <span style={{color: "#666"}}><IntlMessage id={'groupCert.template'}/></span>
                            <Select
                                style={{width: "100%"}}
                                value={readiness.templateId ?? null}
                                placeholder={<IntlMessage id={'groupCert.templatePick'}/>}
                                onChange={(value) => loadReadiness(value)}
                                options={(readiness.templates || []).map(t => ({
                                    value: t.id,
                                    label: t.codePrefix ? `${t.name} (${t.codePrefix})` : t.name
                                }))}
                            />
                            {readiness.nextCode &&
                                <span style={{color: "#666"}}>
                                    <IntlMessage id={'groupCert.nextCode'}/>: <b>{readiness.nextCode}</b>
                                </span>
                            }
                        </Flex>

                        {(readiness.blockers || []).length > 0 &&
                            <Flex vertical gap={4} style={{
                                background: "#fff1f0",
                                border: "1px solid #ffa39e",
                                borderRadius: 8,
                                padding: "10px 14px"
                            }}>
                                {readiness.blockers.map(blocker => (
                                    <span key={blocker} style={{color: "#cf1322"}}>
                                        <IntlMessage id={`groupCert.blocker.${blocker}`}/>
                                    </span>
                                ))}
                            </Flex>
                        }
                    </Flex>
                }
            </Modal>
        </>
    );
};

export default GroupCertificatesButton;
